import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Inject,
  signal,
  WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FileStepperComponent } from './file-stepper/file-stepper.component';
import { Subject } from 'rxjs';
import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocument } from 'pdf-lib';
import printJS from 'print-js';

/*webpackIgnore: true*/
pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.mjs';

export enum FileType {
  PDF,
}

export interface ModalFileViewerData {
  files: Array<{
    name: string;
    blob: Blob;
  }>;
  type: FileType;
}

@Component({
  selector: 'soph-modal-file-viewer',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    forwardRef(() => FileStepperComponent),
    CdkStepperModule,
  ],
  templateUrl: './modal-file-viewer.component.html',
  styleUrl: './modal-file-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalFileViewerComponent {
  public readonly isSendingAction = signal(false);
  public readonly sendAction$: Subject<string | undefined> = new Subject();

  public pdfPreview: WritableSignal<
    Array<{
      imageUrl: string;
      downloadUrl: string;
      name: string;
    }>
  > = signal([]);

  readonly isLoading = signal(false);

  constructor(@Inject(MAT_DIALOG_DATA) public data: ModalFileViewerData) {
    const files = data.files.map((data) => data.blob);

    if (this.isArrayOfBlobs(files) && data.type === FileType.PDF) {
      this.loadAllPdfs(data.files);
    }
  }

  private isArrayOfBlobs(data: Array<Blob>): boolean {
    return Array.isArray(data) && data.every((file) => file instanceof Blob);
  }

  private loadAllPdfs(
    files: Array<{
      name: string;
      blob: Blob;
    }>
  ) {
    this.isLoading.set(true);

    const allImages: Array<Array<{ imageUrl: string; downloadUrl: string; name: string }> | null> =
      new Array(files.length).fill(null);

    let processedCount = 0;

    files.forEach((data, index) => {
      this.processPdf(data, index, allImages, () => {
        processedCount++;

        if (processedCount === files.length) {
          this.pdfPreview.set(
            allImages.filter((item) => item !== null).flat() as Array<{
              imageUrl: string;
              downloadUrl: string;
              name: string;
            }>
          );
          this.isLoading.set(false);
        }
      });
    });
  }

  private processPdf(
    data: {
      name: string;
      blob: Blob;
    },
    index: number,
    allImages: Array<Array<{ imageUrl: string; downloadUrl: string; name: string }> | null>,
    onComplete: () => void
  ) {
    const blobUrl = URL.createObjectURL(data.blob);

    const loadingTask = pdfjsLib.getDocument(blobUrl);

    loadingTask.promise.then((pdf) => {
      const images: { imageUrl: string; downloadUrl: string; name: string }[] = [];

      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        pdf.getPage(pageNumber).then((page) => {
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          if (context) {
            canvas.width = viewport.width;
            canvas.height = viewport.height;

            const renderContext = {
              canvasContext: context,
              viewport: viewport,
            };

            page.render(renderContext).promise.then(() => {
              const imageUrl = canvas.toDataURL('image/png');

              images.push({ imageUrl, downloadUrl: blobUrl, name: data.name });

              if (images.length === pdf.numPages) {
                allImages[index] = images;
                onComplete();
              }
            });
          }
        });
      }
    });
  }

  printPdf() {
    if (this.data && this.data.files && this.data.files.length > 0) {
      this.mergesFiles(this.data.files, 'print', 'resumen-clinico');
    }
  }

  downloadPdf() {
    if (this.data && this.data.files && this.data.files.length > 0) {
      this.mergesFiles(
        this.data.files,
        'download',
        this.data.files.length > 1 ? 'resumen-clinico' : this.data.files[0].name
      );
    }
  }

  sendPdf() {
    this.sendAction$.next(this.data.files.length > 1 ? undefined : this.data.files[0].name);
  }

  async mergesFiles(
    files: Array<{ name: string; blob: Blob }>,
    action: 'download' | 'print' = 'download',
    fileName: string = 'documento'
  ) {
    try {
      const mergedPdf = await PDFDocument.create();

      for (const file of files) {
        const arrayBuffer = await file.blob.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((p) => mergedPdf.addPage(p));
      }

      const mergedBytes = await mergedPdf.save();

      const mergedArray =
        mergedBytes instanceof Uint8Array ? mergedBytes : new Uint8Array(mergedBytes as any);
      const arrayBuffer = mergedArray.buffer.slice(
        mergedArray.byteOffset,
        mergedArray.byteOffset + mergedArray.byteLength
      );
      const mergedBlob = new Blob([arrayBuffer], { type: 'application/pdf' });

      const url = URL.createObjectURL(mergedBlob);
      const baseName = fileName;

      if (action === 'download') {
        this.downloadFile(url, baseName);
      } else if (action === 'print') {
        printJS({ printable: url, type: 'pdf', documentTitle: baseName });
      }
    } catch (error) {
      console.error('Error merging PDFs:', error);
    } finally {
    }
  }

  private downloadFile(url: string, baseName: string) {
    const a = document.createElement('a');
    a.href = url;
    a.download = `${baseName}.pdf`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
